/**
 * Reads the UTR (UPI / bank transaction reference) from a payment screenshot.
 *
 * Receipts label it differently per app: "UTR", "UPI Ref No" (Paytm), "UPI transaction ID"
 * (GPay), "RRN". On UPI it's always 12 digits, so a labelled 12-digit number wins, then any
 * lone 12-digit number, then a labelled alphanumeric reference (bank IMPS/NEFT).
 * App-specific IDs such as PhonePe's "Transaction ID T2306..." are skipped on purpose.
 */

const LABEL = String.raw`(?:UTR|RRN|UPI\s*(?:Ref(?:erence)?|transaction)\.?\s*(?:No|ID|Number)?|Ref(?:erence)?\.?\s*(?:No|Number))`;
const SEPARATOR = String.raw`\.?\s*[:#-]?\s*`;

const LABELLED_UPI_REF = new RegExp(`${LABEL}${SEPARATOR}(\\d{12})(?!\\d)`, 'i');
const LONE_UPI_REF = /(?:^|\D)(\d{12})(?!\d)/;
const LABELLED_BANK_REF = new RegExp(`${LABEL}${SEPARATOR}([A-Z0-9]{6,23})\\b`, 'i');

/** The UTR found in OCR text, or null. OCR often splits digit groups ("3123 4567 8901"). */
export const findUtrInText = (text: string): string | null => {
  const joined = text.replace(/(\d)[ \t]+(?=\d)/g, '$1');
  const match =
    LABELLED_UPI_REF.exec(joined) ?? LONE_UPI_REF.exec(joined) ?? LABELLED_BANK_REF.exec(joined);
  return match?.[1]?.toUpperCase() ?? null;
};

/**
 * OCRs the image in the browser and returns its UTR, or null when none is readable.
 * tesseract.js (~3 MB of wasm and language data) is only downloaded on first use.
 */
export const extractUtrFromImage = async (file: File): Promise<string | null> => {
  if (!file.type.startsWith('image/')) return null;
  const { createWorker } = await import('tesseract.js');
  const worker = await createWorker('eng');
  try {
    const { data } = await worker.recognize(file);
    return findUtrInText(data.text);
  } finally {
    await worker.terminate();
  }
};
